import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
} from '@mui/material';
import {
  Description as PRIcon,
  ShoppingCart as POIcon,
  CheckCircle as CheckIcon,
  Inventory as InventoryIcon,
  Cancel as CancelIcon,
  Pause as HoldIcon,
  Build as RepairIcon,
  LocalShipping as LocalIcon,
  Flight as ForeignIcon,
  HourglassEmpty as QuotationIcon,
  Schedule as RecentIcon,
  Public as HongKongIcon,
  Description,
  LocalShipping,
} from '@mui/icons-material';
import purchaseRequisitionService from '../services/purchaseRequisition.service';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactElement;
  color: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Paper
    sx={{
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      height: 140,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box
        sx={{
          p: 1,
          borderRadius: 2,
          bgcolor: `${color}.light`,
          color: `${color}.main`,
          mr: 2,
          fontSize: '2.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography color="text.secondary" variant="body2">
        {title}
      </Typography>
    </Box>
    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
      {value}
    </Typography>
  </Paper>
);

const PRDashboard: React.FC = () => {
  const [stats, setStats] = useState({
    summary: {
      total_prs: 0,
      pending_count: 0,
      po_created_count: 0,
      received_count: 0,
      cancelled_count: 0,
      hold_count: 0,
      repair_count: 0,
      local_count: 0,
      foreign_count: 0,
      quotation_pending_count: 0,
      waiting_goods_count: 0,
      recent_30days_count: 0,
      local_pr_type_count: 0,
      hongkong_pr_type_count: 0,
    },
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await purchaseRequisitionService.getAll({});
      const data = response.purchaseRequisitions || response;
      
      // Calculate statistics
      const prs = Array.isArray(data) ? data : [];
      const pending = prs.filter((pr: any) => 
        ['waiting_for_pr', 'pr_approved', 'waiting_for_quotation', 'quotation_approved', 'waiting_for_po'].includes(pr.status)
      ).length;
      
      const poCreated = prs.filter((pr: any) => 
        ['po_approved', 'waiting_for_goods'].includes(pr.status)
      ).length;
      
      const received = prs.filter((pr: any) => 
        pr.status === 'good_received'
      ).length;

      const cancelled = prs.filter((pr: any) => pr.status === 'cancel').length;
      const hold = prs.filter((pr: any) => pr.status === 'hold').length;
      const repair = prs.filter((pr: any) => pr.status === 'repair').length;
      
      const local = prs.filter((pr: any) => pr.supplier === 'Local').length;
      const foreign = prs.filter((pr: any) => pr.supplier === 'Foreign').length;
      
      const quotationPending = prs.filter((pr: any) => 
        pr.status === 'waiting_for_quotation'
      ).length;
      
      const waitingGoods = prs.filter((pr: any) => 
        pr.status === 'waiting_for_goods'
      ).length;

      // Recent PRs (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recent30Days = prs.filter((pr: any) => {
        if (!pr.pr_date) return false;
        const prDate = new Date(pr.pr_date);
        return prDate >= thirtyDaysAgo;
      }).length;

      const localPRType = prs.filter((pr: any) => pr.pr_type === 'Local').length;
      const hongkongPRType = prs.filter((pr: any) => pr.pr_type === 'Hong-Kong').length;

      setStats({
        summary: {
          total_prs: prs.length,
          pending_count: pending,
          po_created_count: poCreated,
          received_count: received,
          cancelled_count: cancelled,
          hold_count: hold,
          repair_count: repair,
          local_count: local,
          foreign_count: foreign,
          quotation_pending_count: quotationPending,
          waiting_goods_count: waitingGoods,
          recent_30days_count: recent30Days,
          local_pr_type_count: localPRType,
          hongkong_pr_type_count: hongkongPRType,
        },
      });
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        PR Management Dashboard
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3}>
        {/* Row 1 - Main Status */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total PRs"
            value={stats.summary.total_prs}
            icon={<InventoryIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending PRs"
            value={stats.summary.pending_count}
            icon={<PRIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="PO Created"
            value={stats.summary.po_created_count}
            icon={<POIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Received"
            value={stats.summary.received_count}
            icon={<CheckIcon />}
            color="success"
          />
        </Grid>

        {/* Row 2 - Processing Status */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Quotation Pending"
            value={stats.summary.quotation_pending_count}
            icon={<QuotationIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Waiting for Goods"
            value={stats.summary.waiting_goods_count}
            icon={<LocalShipping />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="On Hold"
            value={stats.summary.hold_count}
            icon={<HoldIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Cancelled"
            value={stats.summary.cancelled_count}
            icon={<CancelIcon />}
            color="error"
          />
        </Grid>

        {/* Row 3 - Supplier & Type Breakdown */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Local Supplier"
            value={stats.summary.local_count}
            icon={<LocalIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Foreign Supplier"
            value={stats.summary.foreign_count}
            icon={<ForeignIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Local PR Type"
            value={stats.summary.local_pr_type_count}
            icon={<Description />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Hong-Kong PR Type"
            value={stats.summary.hongkong_pr_type_count}
            icon={<HongKongIcon />}
            color="secondary"
          />
        </Grid>

        {/* Row 4 - Additional Stats */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Repair"
            value={stats.summary.repair_count}
            icon={<RepairIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Recent (30 Days)"
            value={stats.summary.recent_30days_count}
            icon={<RecentIcon />}
            color="info"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PRDashboard;
